import * as React from "react";

import {
  IS_PASSWORD_RECOVERY,
  redirectRecoveryToWeb,
  redirectToLogin,
  supabase,
} from "./supabase";

/**
 * Blocks render until a Supabase session exists. On load, `detectSessionInUrl`
 * (see supabase.ts) consumes the magic-link hash; this gate then strips the
 * hash from the URL and renders the app. No session → bounce to in-app login.
 */
// Local dev: the API's DEV_AUTH fallback accepts tokenless requests (see
// workers/api middleware/auth.ts) — so skip the gate entirely, even when
// Supabase creds are configured, rather than requiring a real magic-link
// login on every local iteration.
const DEV_AUTH_BYPASS = import.meta.env.DEV;

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<"loading" | "authed">(
    DEV_AUTH_BYPASS ? "authed" : "loading",
  );

  React.useEffect(() => {
    if (DEV_AUTH_BYPASS) {
      return;
    }
    let active = true;
    let redirecting = false;

    const sendToLogin = () => {
      if (active && !redirecting) {
        redirecting = true;
        redirectToLogin();
      }
    };

    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (!active) {
          return;
        }
        if (error || !data.session) {
          sendToLogin();
          return;
        }
        if (IS_PASSWORD_RECOVERY) {
          redirecting = true;
          redirectRecoveryToWeb(data.session);
          return;
        }
        if (window.location.hash.includes("access_token")) {
          window.history.replaceState(
            {},
            "",
            window.location.pathname + window.location.search,
          );
        }
        setState("authed");
      })
      .catch(sendToLogin);

    // React to sign-out / token loss from any tab.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) {
        redirecting = true;
        redirectRecoveryToWeb(session);
        return;
      }
      if (!session) {
        sendToLogin();
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state === "loading") {
    return null;
  }
  return <>{children}</>;
}
